/**
 * Take a new build deliberately (round 8.1, task 1; revised in fix round 1).
 *
 * `registerType: 'prompt'` (vite.config.ts) leaves a newly installed service
 * worker sitting in `waiting` — nothing activates it on its own — and calls
 * this module's `onNeedRefresh()` the moment that happens
 * (`virtual:pwa-register`'s own hook, reachable because `injectRegister:
 * false` means this module is the one calling `registerSW`, not the plugin's
 * injected script). Activating the waiting worker is a message the client
 * has to send: calling the `updateSW()` this module gets back from
 * `registerSW` posts `SKIP_WAITING` to it — `dist/sw.js`'s own message
 * listener (present in every generateSW build, prompt or not) is what turns
 * that into `self.skipWaiting()`. The worker activates, takes over the page
 * it was already controlling, and `virtual:pwa-register`'s own `controlling`
 * listener reloads for us — nothing here calls `window.location.reload()`.
 *
 * So the only decision that lives here is WHEN to call `updateSW()`:
 * immediately, unless the surveyor is mid-keystroke, looking at an open
 * dialog, or out in the OS camera (`shouldReloadNow`, src/ui/state/
 * appUpdate.ts) — in which case the call is deferred to whichever comes
 * first: the next screen change (`hashchange`) or the next time the tab goes
 * into the background (`visibilitychange`), each re-checked through the same
 * predicate, because the field that mattered may not be there anymore — and
 * because, on iOS, going into the background is what presenting the camera
 * or the photo picker from `<input type="file">` does (final review's fix
 * wave, D-040 postscript): before the re-check, a deferred build reloaded
 * under the picker and the picture was lost. Never on `focusout` — that
 * fires on every ordinary move between fields and would reload the form out
 * from under her mid-survey.
 */

import { registerSW } from 'virtual:pwa-register';

import { isCaptureInFlight } from './captureInFlight';
import { shouldReloadNow, type ReloadContext } from './state/appUpdate';

/** The tag name of whatever the surveyor is focused in, or `null`. */
function activeTag(): string | null {
  const active = document.activeElement;
  return active instanceof HTMLElement ? active.tagName : null;
}

/** Whether a `[role="dialog"]` is open anywhere on the page right now. */
function hasOpenDialog(): boolean {
  return document.querySelector('[role="dialog"]') !== null;
}

function currentContext(): ReloadContext {
  return { activeTag: activeTag(), hasOpenDialog: hasOpenDialog(), capturing: isCaptureInFlight() };
}

/**
 * Register the service worker once, and take a new build the moment it is
 * safe to. Call exactly once, from `src/main.tsx`.
 */
export function registerAppUpdate(): void {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;

  const updateSW = registerSW({
    immediate: true,
    onNeedRefresh() {
      if (shouldReloadNow(currentContext())) {
        void updateSW();
        return;
      }

      // Deferred: the first of a screen change or the tab going to the
      // background wins, each re-checked against the same predicate; both
      // listeners are torn down once one of them takes the build, and both
      // stay armed for as long as neither may.
      const take = (): void => {
        window.removeEventListener('hashchange', onHashChange);
        document.removeEventListener('visibilitychange', onVisibilityChange);
        void updateSW();
      };
      const onHashChange = (): void => {
        if (shouldReloadNow(currentContext())) take();
      };
      const onVisibilityChange = (): void => {
        // Re-checked, not taken outright: a hidden page is the OS camera on
        // iOS, and a capture in flight (or a field still focused under a
        // sheet) is exactly what a reload here would lose.
        if (document.visibilityState === 'hidden' && shouldReloadNow(currentContext())) take();
      };
      window.addEventListener('hashchange', onHashChange);
      document.addEventListener('visibilitychange', onVisibilityChange);
    },
  });
}
